import { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Mail, Lock } from "lucide-react";
import ChatList from "./ChatList/ChatList";
import Chat from "./Chat/Chat";
import { useConversations } from "../../hooks/messages/useMessages";
import { motion } from "framer-motion";
import socket from "../../services/socketClient";
import AnimatedLoadingScreen from "../../components/UIFeedback/AnimatedLoadingScreen/AnimatedLoadingScreen";
import ErrorScreen from "../../components/UIFeedback/ErrorScreen/ErrorScreen";
import { Button } from "../../components/ui/button";
import { useSelector } from "react-redux";

const Messages = () => {
  const { t } = useTranslation();
  const { chatId } = useParams();
  const navigate = useNavigate();
  const { conversations, conversationsLoading, error, refetchConversations } = useConversations();
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);

  useEffect(() => {
    refetchConversations();

    socket.on("newConversation");

    return () => {
      socket.off("newConversation");
    };
  }, [refetchConversations]);

  const handleBackToChatList = () => {
    refetchConversations();
    navigate("/messages");
  };

  if (conversationsLoading) {
    return <AnimatedLoadingScreen message={t('loading_messages')} />;
  }

  if (error) {
    return (
      <ErrorScreen>
        <p>{error.message || t('error_occurred_try_again')}</p>
      </ErrorScreen>
    );
  }

  // Not logged in state - matching reference design
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4 sm:p-6 pt-16 sm:pt-20">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
          className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-primary/10 flex items-center justify-center mb-4 sm:mb-6"
        >
          <Lock className="w-10 h-10 sm:w-12 sm:h-12 text-primary" />
        </motion.div>
        
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
          className="text-xl sm:text-2xl font-bold text-foreground mb-2 sm:mb-3 text-center"
        >
          {t('sign_in_to_view_messages', 'Sign in to view messages')}
        </motion.h2>
        
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.2 }}
          className="text-center text-xs sm:text-sm text-muted-foreground mb-6 sm:mb-8 max-w-sm px-2"
        >
          {t('messages_login_description', 'Create an account to message pet owners, sitters, and community members')}
        </motion.p>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.3 }}
        >
          <Button
            onClick={() => navigate('/login')}
            className="bg-primary hover:bg-primary/90 text-primary-foreground px-6 sm:px-8 py-2.5 sm:py-3 rounded-xl text-sm sm:text-base font-semibold"
          >
            {t('sign_in', 'Sign In')}
          </Button>
        </motion.div>

        {/* Feature list */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.4 }}
          className="mt-8 sm:mt-12 space-y-2.5 sm:space-y-3 w-full max-w-sm px-2"
        >
          <p className="text-[10px] sm:text-xs font-semibold text-foreground uppercase tracking-wide">
            {t('message_features', 'Message features:')}
          </p>
          <ul className="space-y-1.5 sm:space-y-2">
            <li className="flex items-start gap-2 sm:gap-3">
              <div className="w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <svg className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <span className="text-xs sm:text-sm text-muted-foreground">
                {t('feature_contact_owners', 'Contact lost & found animal owners')}
              </span>
            </li>
            <li className="flex items-start gap-2 sm:gap-3">
              <div className="w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <svg className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <span className="text-xs sm:text-sm text-muted-foreground">
                {t('feature_connect_sitters', 'Connect with pet sitters')}
              </span>
            </li>
            <li className="flex items-start gap-2 sm:gap-3">
              <div className="w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <svg className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <span className="text-xs sm:text-sm text-muted-foreground">
                {t('feature_secure_messaging', 'Secure in-app messaging')}
              </span>
            </li>
          </ul>
        </motion.div>
      </div>
    );
  }

  // When in chat view, use fixed height to prevent page scroll
  if (chatId) {
    return (
      <div className="bg-background overflow-hidden" style={{ height: '100dvh' }}>
        <Chat 
          chat={conversations.find((c) => c._id === chatId)} 
          onBack={handleBackToChatList}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {conversations.length > 0 ? (
        <div>
          <ChatList chats={conversations} onSelectChat={(chat) => navigate(`/messages/${chat._id}`)} />
        </div>
      ) : (
        // No messages empty state
        <div className="flex flex-col items-center justify-center min-h-[60vh] p-4 sm:p-6">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-secondary flex items-center justify-center mb-3 sm:mb-4"
          >
            <Mail className="w-8 h-8 sm:w-10 sm:h-10 text-primary" />
          </motion.div>
          <motion.h2
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-xl sm:text-2xl font-bold text-foreground mb-2 text-center"
          >
            {t('no_chats_yet', 'No Messages')}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="text-center text-xs sm:text-sm text-muted-foreground px-4"
          >
            {t('conversations_appear_here', 'Your conversations will appear here')}
          </motion.p>
        </div>
      )}
    </div>
  );
};

export default Messages;
